import type { components } from '@connected/api-client/api.generated';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    SingleHorizontalOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Input } from '@connected/ui/components/input';
import { Textarea } from '@connected/ui/components/textarea';
import { useTranslation } from 'react-i18next';

import type { CompletionFormValues } from '../utils/completion-types';

import { useCompletionReportPatch } from '../hooks/use-completion-mutations';
import { INTERVAL_QUESTION_INSPECTION_KINDS } from '../utils/completion-types';
import { IntervalQuestionsFields } from './interval-questions-fields';
import { Scope10InspectionTable } from './scope10-inspection-table';

type CompletionReportFieldsProps = {
    form: FormApi<CompletionFormValues, ZodValidator> & ReactFormApi<CompletionFormValues, ZodValidator>;
    reportIndex: number;
    inspectionKind: components['schemas']['ElectricalInspectionKind'];
    projectVersionId: string;
    conclusionData: components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'] | undefined;
    /** Whether to show interval questions in this component (false if rendered at form level) */
    showIntervalQuestions?: boolean;
};

export function CompletionReportFields({
    form,
    reportIndex,
    inspectionKind,
    projectVersionId,
    conclusionData,
    showIntervalQuestions = true,
}: CompletionReportFieldsProps) {
    const { t } = useTranslation('re-inspections');

    // Hook for patching report-level fields
    const { patchField: reportPatchField } = useCompletionReportPatch({
        projectVersionId,
        inspectionKind,
    });

    // Get report-based field name
    function getFieldName<T extends string>(field: T) {
        return `reports[${reportIndex}].${field}` as const;
    }

    // Check if this inspection kind uses interval questions
    const hasIntervalQuestions = INTERVAL_QUESTION_INSPECTION_KINDS.includes(inspectionKind);

    return (
        <div className="flex flex-col gap-4">
            {/* Interval Questions section - only for inspection kinds that use them and when showIntervalQuestions is true */}
            {showIntervalQuestions && hasIntervalQuestions && (
                <IntervalQuestionsFields
                    form={form}
                    projectVersionId={projectVersionId}
                    conclusionData={conclusionData}
                />
            )}

            {/* intervalDeterminedByKind field */}
            <form.Field
                name={getFieldName('intervalDeterminedByKind')}
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.intervalDeterminedByKind,
                    onChangeAsync: reportPatchField('IntervalDeterminedByKind'),
                    onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.decidedFor.label')}
                        className="w-full"
                        htmlFor={field.name}
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: t('completion.form.decidedFor.insurance.label'), value: 'InsuranceCompany' },
                                { label: t('completion.form.decidedFor.inspection.label'), value: 'Inspection' },
                                { label: t('completion.form.decidedFor.rentalAgreement.label'), value: 'RentalContract' },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>

            {/* Conditional Scope10Inspection table OR nextInspectionInYearCount field */}
            <form.Subscribe
                selector={state => ({
                    intervalDeterminedByKind: state.values.reports?.[reportIndex]?.intervalDeterminedByKind,
                })}
            >
                {({ intervalDeterminedByKind }) => conclusionData?.Inspection?.Scope10Inspection?.Enabled && intervalDeterminedByKind === 'Inspection' && inspectionKind === 'Scope10'
                    ? (
                            <Scope10InspectionTable
                                tableData={conclusionData?.Inspection?.Scope10Inspection}
                                projectVersionId={projectVersionId}
                                onCellValueChange={value => form.setFieldValue(getFieldName('nextInspectionInYearCount'), value)}
                            />
                        )
                    : (
                            <form.Field
                                name={getFieldName('nextInspectionInYearCount')}
                                validators={{
                                    onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.nextInspectionInYearCount,
                                    onChangeAsync: reportPatchField('NextInspectionInYearCount'),
                                    onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                                }}
                            >
                                {field => (
                                    <Label
                                        label="Volgende inspectie over jaar"
                                        className="w-full"
                                        htmlFor={field.name}
                                    >
                                        <NumberField
                                            name={field.name}
                                            onChange={field.handleChange}
                                            value={field.state.value}
                                            min={0}
                                        />
                                        <FieldInfo state={field.state} />
                                    </Label>
                                )}
                            </form.Field>
                        )}
            </form.Subscribe>

            {/* sciosInspectionCode field */}
            {conclusionData?.Inspection?.ScopeInspection?.Enabled && (
                <form.Field
                    name={getFieldName('sciosInspectionCode')}
                    validators={{
                        onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.sciosInspectionCode,
                        onChangeAsync: reportPatchField('SciosInspectionCode'),
                        onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                    }}
                >
                    {field => (
                        <Label label={t('completion.form.sciosSignOutCode.label')} className="w-full">
                            <Input
                                name={field.name}
                                onChange={e => field.handleChange(e.target.value)}
                                value={field.state.value ?? ''}
                                className="flex flex-row items-center justify-between"
                                placeholder={t('completion.form.sciosSignOutCode.placeholder')}
                            />
                            <FieldInfo state={field.state} />
                        </Label>
                    )}
                </form.Field>
            )}

            {/* includeRepairStatement field */}
            <form.Field
                name={getFieldName('includeRepairStatement')}
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.includeRepairStatement,
                    onChangeAsync: reportPatchField('IncludeRepairStatement'),
                    onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.recoveryStatement.label')}
                        className="w-full"
                        htmlFor={field.name}
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: t('words.yes', { ns: 'common' }), value: true },
                                { label: t('words.no', { ns: 'common' }), value: false },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>

            {/* isPerformedAccordingToInspectionPlan field */}
            <form.Field
                name={getFieldName('isPerformedAccordingToInspectionPlan')}
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.isPerformedAccordingToInspectionPlan,
                    onChangeAsync: reportPatchField('IsPerformedAccordingToInspectionPlan'),
                    onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label="Is de inspectie conform inspectieplan verlopen?"
                        className="w-full"
                        htmlFor={field.name}
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: t('words.yes', { ns: 'common' }), value: true },
                                { label: t('words.no', { ns: 'common' }), value: false },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>

            {/* Conditional remark field when isPerformedAccordingToInspectionPlan is false */}
            <form.Subscribe
                selector={state => ({
                    isPerformedAccordingToInspectionPlan: state.values.reports?.[reportIndex]?.isPerformedAccordingToInspectionPlan,
                })}
            >
                {({ isPerformedAccordingToInspectionPlan }) => isPerformedAccordingToInspectionPlan === false && (
                    <form.Field
                        name={getFieldName('performedAccordingToInspectionPlanRemark')}
                        validators={{
                            onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.performedAccordingToInspectionPlanRemark,
                            onChangeAsync: reportPatchField('PerformedAccordingToInspectionPlanRemark'),
                            onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                        }}
                    >
                        {field => (
                            <Label
                                label="Toelichting"
                                className="w-full"
                                htmlFor={field.name}
                            >
                                <Textarea
                                    name={field.name}
                                    onChange={e => field.handleChange(e.target.value)}
                                    value={field.state.value ?? ''}
                                    placeholder="Geef een toelichting waarom de inspectie niet conform het inspectieplan is verlopen"
                                />
                                <FieldInfo state={field.state} />
                            </Label>
                        )}
                    </form.Field>
                )}
            </form.Subscribe>

            {/* isFinalVerdictPassed field (Eindoordeel) */}
            <form.Field
                name={getFieldName('isFinalVerdictPassed')}
                validators={{
                    onChange: validators.patchElectricalProjectInspectionConclusionReportCommand.isFinalVerdictPassed,
                    onChangeAsync: reportPatchField('IsFinalVerdictPassed'),
                    onChangeAsyncDebounceMs: reportPatchField.debounceTime,
                }}
            >
                {field => (
                    <Label
                        label={t('completion.form.verdict.label')}
                        className="w-full"
                        htmlFor={field.name}
                    >
                        <SingleOptionField
                            name={field.name}
                            handleChange={field.handleChange}
                            state={field.state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: t('completion.form.suffices.label'), value: true },
                                { label: t('completion.form.doesnt-suffice.label'), value: false },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>
        </div>
    );
}
